import React, { useMemo, useState, useEffect } from 'react';
import { Layout, Menu, Button, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { menuItems } from '../menuConfig';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';

const { Sider } = Layout;

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasMenuPermission } = useAuth();
  const { t } = useLocale();
  const selectedKey = location.pathname;

  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const visibleMenuItems = useMemo(() => {
    const filterByPermission = (items: typeof menuItems): typeof menuItems =>
      items
        .map((item) => {
          if (item.children) {
            const visibleChildren = filterByPermission(item.children);
            if (visibleChildren.length > 0) {
              return { ...item, children: visibleChildren };
            }
            if (hasMenuPermission(item.key)) {
              return { ...item, children: undefined };
            }
            return null;
          }
          return hasMenuPermission(item.key) ? item : null;
        })
        .filter(Boolean) as typeof menuItems;

    return filterByPermission(menuItems);
  }, [hasMenuPermission]);

  useEffect(() => {
    if (collapsed) {
      setOpenKeys([]);
    } else {
      const parent = visibleMenuItems.find((m) =>
        m.children?.some((c) => c.path === selectedKey)
      );
      const parentKey = parent?.key;
      if (parentKey) setOpenKeys([parentKey]);
    }
  }, [collapsed, selectedKey, visibleMenuItems]);

  const handleOpenChange: MenuProps['onOpenChange'] = (keys) => {
    setOpenKeys(keys as string[]);
  };

  const antdItems = useMemo(() => {
    const build = (items: typeof menuItems): MenuProps['items'] =>
      items.map((item) => {
        if (item.children) {
          return {
            key: item.key,
            icon: item.icon,
            label: t(item.label),
            children: build(item.children),
          };
        }
        // 菜单名加 tooltip，任何状态下都能看到完整名称：
        // - 折叠态：只剩图标，不悬停根本不知道是哪一项（antd 的 inlineCollapsed 只会给
        //   **纯字符串** label 自动加 title，这里的 label 是 <a> 元素，所以它不会加）
        // - 展开态：中文最长的「年终分红（完善中）」已接近 200px 栏宽，
        //   英文（如 "Year-end Bonus (In Progress)"）更会溢出被截断
        // 折叠态必须立刻出（否则悬停没有反馈），展开态加延迟避免划过时频繁弹窗。
        // 带 children 的父项不加：折叠时它本来就靠 antd 的弹出子菜单交互，再加 tooltip 会打架。
        const labelText = t(item.label);
        const withTip = (node: React.ReactNode) => (
          <Tooltip
            title={labelText}
            placement="right"
            arrow={{ pointAtCenter: true }}
            mouseEnterDelay={collapsed ? 0 : 0.6}
          >
            <span style={{ display: 'block' }}>{node}</span>
          </Tooltip>
        );
        return {
          key: item.path || item.key,
          icon: item.icon,
          label: item.path ? withTip(
            <a
              href={item.path}
              onClick={(e) => {
                if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
                  e.preventDefault();
                  navigate(item.path!);
                }
              }}
              style={{ color: 'inherit', textDecoration: 'none', display: 'block' }}
            >
              {labelText}
            </a>
          ) : withTip(labelText),
        };
      });
    return build(visibleMenuItems);
  }, [navigate, t, visibleMenuItems, collapsed]);

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      collapsedWidth={64}
      width={200}
      style={{
        background: '#0B5394',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 200,
        overflow: 'hidden',
        boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
      }}
    >
      {/* Logo区域 */}
      <div
        style={{
          height: 64,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          padding: collapsed ? 0 : '0 16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {!collapsed && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            <span
              style={{
                fontSize: 17,
                fontWeight: 800,
                color: '#fff',
                letterSpacing: 0.5,
                lineHeight: 1.2,
              }}
            >
              JDSUIT AGI
            </span>
            <span
              style={{
                fontSize: 'var(--font-xs)',
                color: 'rgba(255,255,255,0.7)',
                letterSpacing: 1,
                lineHeight: 1.2,
                marginTop: 2,
                fontWeight: 500,
              }}
            >
              ORDER SYSTEM V1.5
            </span>
          </div>
        )}
        <Button size="middle"
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => onCollapse(!collapsed)}
          style={{ color: 'rgba(255,255,255,0.85)', fontSize: 'var(--font-lg)' }}
        />
      </div>

      {/* 菜单区域 */}
      <div
        className="sidebar-menu-scroll"
        style={{
          flex: 1,
          overflow: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          maxHeight: 'calc(100vh - 64px)',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.15) transparent',
        }}
      >
        <Menu
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={[selectedKey]}
          openKeys={openKeys}
          items={antdItems}
          onOpenChange={handleOpenChange}
          className="jds-sidebar-menu"
          style={{
            borderRight: 0,
            background: 'transparent',
            paddingTop: 8,
          }}
          theme="dark"
        />
      </div>
    </Sider>
  );
};

export default Sidebar;
